import { createContext, useContext, useEffect, useState } from "react";

// Define the available themes based on index.css and DaisyUI defaults
type Theme = "bumblebee" | "dark" | "light" | "cupcake" | "emerald" | "corporate" | "synthwave" | "retro" | "cyberpunk" | "valentine" | "halloween" | "garden" | "forest" | "aqua" | "lofi" | "pastel" | "fantasy" | "wireframe" | "black" | "luxury" | "dracula" | "cmyk" | "autumn" | "business" | "acid" | "lemonade" | "night" | "coffee" | "winter" | "dim" | "nord" | "sunset";

// Array of all available themes for iteration
const availableThemes: Theme[] = [
  "bumblebee", "dark", "light", "cupcake", "emerald", "corporate", "synthwave", "retro", "cyberpunk", "valentine", "halloween", "garden", "forest", "aqua", "lofi", "pastel", "fantasy", "wireframe", "black", "luxury", "dracula", "cmyk", "autumn", "business", "acid", "lemonade", "night", "coffee", "winter", "dim", "nord", "sunset"
];

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
  // You might pass the user's stored theme preference here later
  // userDbTheme?: Theme | null;
};

type ThemeProviderState = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  availableThemes: Theme[];
};

const initialState: ThemeProviderState = {
  theme: "bumblebee",
  setTheme: () => null,
  availableThemes: availableThemes,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
  children,
  defaultTheme = "bumblebee",
  storageKey = "vite-ui-theme",
  // userDbTheme = null, // Example prop for user preference
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(() => {
    // TODO: Step 1: Check for user preference from DB (when implemented)
    // Example: 
    // if (userDbTheme && availableThemes.includes(userDbTheme)) {
    //   return userDbTheme;
    // }

    // Step 2: Check localStorage
    try {
      const storedTheme = localStorage.getItem(storageKey) as Theme;
      if (storedTheme && availableThemes.includes(storedTheme)) {
        return storedTheme;
      }
    } catch (error) {
      console.error("Error reading theme from localStorage", error);
    }

    // Step 3: Fallback to default theme
    return defaultTheme;
  });

  useEffect(() => {
    // Apply the theme to the html tag using data-theme attribute
    // This ensures compatibility with theme-controller which expects this attribute
    const root = window.document.documentElement;
    root.setAttribute('data-theme', theme);

    // No longer need to manually add/remove light/dark classes
  }, [theme]);

  const value = {
    theme,
    setTheme: (newTheme: Theme) => {
      if (availableThemes.includes(newTheme)) {
        try {
            localStorage.setItem(storageKey, newTheme);
        } catch (error) {
            console.error("Error writing theme to localStorage", error);
        }
        setTheme(newTheme); // This triggers the useEffect to update data-theme
        // TODO: Also update user preference in DB here (when implemented)
      } else {
        console.warn(`Attempted to set invalid theme: ${newTheme}`);
      }
    },
    availableThemes,
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined)
    throw new Error("useTheme must be used within a ThemeProvider");

  return context;
};
